import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi";

import { assetLogo } from "../../constant";

type AssetOption = {
  symbol: string;
  image: string;
};

const OPTIONS: AssetOption[] = assetLogo.map((a) => ({
  symbol: a.name.toUpperCase(),
  image: a.image,
}));

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
};

const RequiredPairsSelect: React.FC<Props> = ({
  value,
  onChange,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () =>
      value
        .map((sym) => OPTIONS.find((o) => o.symbol === sym.toUpperCase()))
        .filter(Boolean) as AssetOption[],
    [value]
  );

  const available = useMemo(() => {
    const q = query.trim().toUpperCase();
    const selectedSet = new Set(value.map((v) => v.toUpperCase()));
    return OPTIONS.filter((o) => {
      if (selectedSet.has(o.symbol)) return false;
      if (!q) return true;
      return o.symbol.includes(q);
    });
  }, [query, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const add = (symbol: string) => {
    const u = symbol.toUpperCase();
    if (value.map((v) => v.toUpperCase()).includes(u)) return;
    onChange([...value, u]);
    setQuery("");
  };

  const remove = (symbol: string) => {
    const u = symbol.toUpperCase();
    onChange(value.filter((v) => v.toUpperCase() !== u));
  };

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        className="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-lg border border-[#2C363E] bg-[#161E25] px-2.5 py-1.5 text-left outline-none transition focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40 disabled:opacity-50"
      >
        {selected.length === 0 ? (
          <span className="px-1 text-[13px] text-[#5C6B76]">
            Search and add coins…
          </span>
        ) : (
          selected.map((s) => (
            <span
              key={s.symbol}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#3A4852] bg-[#1C2227] py-0.5 pl-1.5 pr-1 text-[12px] font-semibold text-white"
            >
              <img
                src={s.image}
                alt=""
                className="h-4 w-4 rounded-full object-contain"
              />
              {s.symbol}
              <span
                role="button"
                tabIndex={0}
                aria-label={`Remove ${s.symbol}`}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(s.symbol);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    remove(s.symbol);
                  }
                }}
                className="rounded-full p-0.5 text-[#869AA9] hover:bg-[#273037] hover:text-white"
              >
                <FiX className="text-xs" />
              </span>
            </span>
          ))
        )}
        <FiChevronDown
          className={`ml-auto shrink-0 text-[#869AA9] transition ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-xl border border-[#2C363E] bg-[#13181B] shadow-[0_16px_40px_rgba(0,0,0,0.45)]">
          <div className="flex items-center gap-2 border-b border-[#2C363E] px-3 py-2">
            <FiSearch className="shrink-0 text-[#869AA9]" aria-hidden />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search BTC, USDT, USDC…"
              className="w-full bg-transparent text-[13px] text-[#E7EBEE] outline-none placeholder:text-[#5C6B76]"
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setOpen(false);
                  setQuery("");
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (available[0]) add(available[0].symbol);
                }
              }}
            />
          </div>
          <ul
            role="listbox"
            className="max-h-56 overflow-y-auto overscroll-contain py-1"
          >
            {available.length === 0 ? (
              <li className="px-3 py-3 text-center text-[12px] text-[#869AA9]">
                {OPTIONS.every((o) =>
                  value.map((v) => v.toUpperCase()).includes(o.symbol)
                )
                  ? "All coins selected"
                  : "No coins match"}
              </li>
            ) : (
              available.map((opt) => (
                <li key={opt.symbol}>
                  <button
                    type="button"
                    role="option"
                    onClick={() => add(opt.symbol)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-[#1C2227]"
                  >
                    <img
                      src={opt.image}
                      alt=""
                      className="h-6 w-6 rounded-full object-contain"
                    />
                    <span className="text-[13px] font-semibold text-white">
                      {opt.symbol}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RequiredPairsSelect;
